import { Listbox } from '@headlessui/react';
import classNames from 'classnames';
import { ClippedText } from 'shared/ui/ClippedText';
import type { DataAttributes } from 'shared/lib/data-auto-test';
import type { Option } from 'shared/ui/MultiSelect/interfaces';
import style from 'shared/ui/MultiSelect/Components/Menu.module.scss';

interface ListOptionProps {
  option: Option;
  className?: string;
  renderOption?: (selected: boolean) => React.ReactNode;
}

export function ListOption(props: ListOptionProps) {
  const { option, renderOption, className } = props;

  return (
    <Listbox.Option
      className={classNames(style.option, className)}
      value={option}
    >
      {({ selected }) => (
        <span className="flex items-center justify-start overflow-hidden">
          {renderOption && <span className="pointer-events-none flex">{renderOption(selected)}</span>}
          <ClippedText
            as="span"
            text={option.text}
            className="ml-1 break-keep font-medium"
            tooltip={{
              placement: 'bottom-end',
              content: option.text,
              widthClass: 'max-w-full',
            }}
          />
        </span>
      )}
    </Listbox.Option>
  );
}

type OptionComponentProps<TOption extends Option> = {
  option: TOption;
  className?: string;
  selected?: boolean;
  renderOption?: (selected?: boolean) => React.ReactNode;
  attributes?: DataAttributes['attributes'];
  handleSelect: (option: TOption, e: React.MouseEvent<HTMLLIElement>) => void;
  showFullText?: boolean;
};

export function ListOptionComponent<TOption extends Option>({
  attributes,
  option,
  className,
  handleSelect,
  selected,
  renderOption,
  showFullText = false,
}: OptionComponentProps<TOption>): JSX.Element {
  const handleOptionClick = (e: React.MouseEvent<HTMLLIElement>) => {
    handleSelect(option, e);
  };

  return (
    <li
      {...attributes}
      role="option"
      aria-selected={selected}
      aria-disabled={option.disabled}
      className={classNames(style.option, className, 'hover:bg-deep-blue-25', {
        'pointer-events-none': option.disabled,
        'max-h-14': !showFullText,
      })}
      onClick={handleOptionClick}
    >
      <span
        className={classNames(
          'flex items-center justify-start overflow-hidden',
          showFullText ? 'break-words' : 'break-all'
        )}
      >
        {renderOption && <span className="pointer-events-none flex">{renderOption(selected)}</span>}
        {showFullText ? (
          <span className={classNames('ml-1 font-medium', { 'text-slate-200': option.disabled })}>{option.text}</span>
        ) : (
          <ClippedText
            text={option.text}
            className={classNames('ml-1 font-medium', { 'text-slate-200': option.disabled })}
            tooltip={{
              placement: 'bottom-end',
              content: option.text,
              widthClass: 'max-w-full',
            }}
          />
        )}
      </span>
    </li>
  );
}
